import { IImage } from '@models/IImage';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import classNames from 'classnames';
import { PropsWithChildren, RefObject, useState } from 'react';

interface Props {
  readonly classes?: string;
  readonly dataTestId?: string;
  readonly epiEdit?: string;
  readonly image?: IImage;
  readonly imageClasses?: string;
  readonly imageRef?:
    | RefObject<HTMLImageElement>
    | ((node: HTMLElement | null) => void);
  readonly lazyLoad?: boolean;
  readonly progressiveLoad?: boolean;
  readonly sizes?: string;
}

const Image = ({
  children,
  classes,
  dataTestId,
  epiEdit,
  image,
  imageClasses,
  imageRef,
  lazyLoad = true,
  progressiveLoad = true,
  sizes = '',
}: PropsWithChildren<Props>) => {
  const mobileSource = image?.mobile;
  const tabletSource = image?.tablet;
  const desktopSource = image?.desktop;
  const largeDesktopSource = image?.largeDesktop;
  const breakpoint = useBreakpoint() as BreakpointEnum;
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  if (!desktopSource?.url) return null;

  const currentSrc = {
    [BreakpointEnum.Small]: mobileSource,
    [BreakpointEnum.Medium]: tabletSource,
    [BreakpointEnum.Large]: desktopSource,
    [BreakpointEnum.XLarge]: largeDesktopSource,
  };

  return (
    <picture className={classNames(classes)}>
      <source
        type="image/webp"
        sizes={sizes}
        srcSet={`${mobileSource?.url} 376w, ${tabletSource?.url} 768w, ${desktopSource?.url} 1024w, ${largeDesktopSource?.url} 1440w`}
      />
      {/* biome-ignore lint/a11y/useAltText: alt is conditionally applied with fallback */}
      <img
        ref={imageRef}
        alt={image?.altText?.length ? image?.altText : 'nikon-image'}
        className={classNames(
          imageClasses,
          'transition duration-500 ease-in-out'
        )}
        data-epi-property-name={epiEdit}
        draggable="false"
        loading={lazyLoad ? 'lazy' : 'eager'}
        onLoad={() => {
          setIsLoaded(true);
        }}
        sizes={sizes}
        src={
          isLoaded && progressiveLoad
            ? currentSrc[breakpoint]?.url
            : mobileSource?.url
        }
        height={currentSrc[breakpoint]?.height}
        width={currentSrc[breakpoint]?.width}
        srcSet={`${mobileSource?.originalUrl} 376w, ${tabletSource?.originalUrl} 768w, ${desktopSource?.originalUrl} 1024w, ${largeDesktopSource?.originalUrl} 1440w`}
        {...(dataTestId && { 'data-testid': dataTestId })}
      />
      {isLoaded && children}
    </picture>
  );
};

export default Image;
